import { readEnv } from '../config/env';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../services/api';
import { PKCE } from '../utils/pkce';
import { ensureRecaptchaLoaded } from '../utils/recaptcha';
import { capitalizeError } from '../utils/errorUtils';

interface AuthModalProps {
  type: 'login' | 'register';
  isOpen: boolean;
  onClose: () => void;
}

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      render: (
        element: HTMLElement | string,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

const AuthModal: React.FC<AuthModalProps> = ({ type, isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const captchaWidgetIdRef = useRef<number | null>(null);
  // Клик по оверлею закрывает только если нажатие и отпускание были на оверлее.
  const overlayPressRef = useRef(false);

  const recaptchaSiteKey = readEnv('REACT_APP_RECAPTCHA_SITE_KEY');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation requiring domain and TLD (e.g., user@example.com)
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Enter a valid email with domain, e.g., user@example.com');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Проверяем наличие CAPTCHA токена (для v2 токен получается через callback виджета)
      if (recaptchaSiteKey && !captchaToken) {
        setError('Please complete the CAPTCHA verification');
        setIsLoading(false);
        return;
      }

      const response = await auth.requestAuthLink(trimmedEmail, captchaToken);

      if (response.success && response.data) {
        setIsSuccess(true);
        // Сбрасываем CAPTCHA после успешной отправки
        if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
          setCaptchaToken('');
        }
      } else {
        setError(capitalizeError(response.error?.message) || 'Error occurred while sending link');
        // Сбрасываем CAPTCHA при ошибке
        if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
          setCaptchaToken('');
        }
      }
    } catch (err: any) {
      setError(
        capitalizeError(err.response?.data?.error?.message || err.response?.data?.message) ||
          'Error occurred while sending link'
      );
      // Сбрасываем CAPTCHA при ошибке
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetIdRef.current);
        setCaptchaToken('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Генерируем PKCE данные
      const codeVerifier = PKCE.generateCodeVerifier();
      const codeChallenge = await PKCE.generateCodeChallenge(codeVerifier);
      const state = PKCE.generateState();

      // Сохраняем PKCE данные
      PKCE.savePKCEData(codeVerifier, state);

      // Получаем URL для авторизации от бэкенда
      const response = await auth.getGoogleAuthURL();

      if (response.success && response.data) {
        // Добавляем PKCE параметры к URL
        const authUrl = new URL(response.data.auth_url);
        authUrl.searchParams.set('code_challenge', codeChallenge);
        authUrl.searchParams.set('code_challenge_method', 'S256');
        authUrl.searchParams.set('state', state);

        // Перенаправляем на Google OAuth
        window.location.href = authUrl.toString();
      } else {
        setError(capitalizeError(response.error?.message) || 'Error getting authorization URL');
      }
    } catch (err: any) {
      setError(capitalizeError(err.response?.data?.error?.message) || 'Error initiating Google OAuth');
    } finally {
      setIsLoading(false);
    }
  };

  // Инициализация reCAPTCHA при открытии модалки
  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!isOpen) return;

      // Если reCAPTCHA не настроена, очищаем токен и выходим
      if (!recaptchaSiteKey) {
        setCaptchaToken('');
        return;
      }

      const isReady = await ensureRecaptchaLoaded();
      if (cancelled) return;

      if (!isReady || !window.grecaptcha) {
        // Скрипт мог быть заблокирован adblock/сетью — просто не показываем капчу.
        setCaptchaToken('');
        return;
      }

      // Очищаем предыдущий виджет если есть
      const container = document.getElementById('recaptcha-container');
      if (!container) return;
      container.innerHTML = '';

      window.grecaptcha.ready(() => {
        if (cancelled) return;

        const widgetId = window.grecaptcha.render('recaptcha-container', {
          sitekey: recaptchaSiteKey,
          callback: (token: string) => {
            setCaptchaToken(token);
            // Очищаем ошибку CAPTCHA после успешного прохождения
            setError((prevError) => {
              if (prevError === 'Please complete the CAPTCHA verification') {
                return '';
              }
              return prevError;
            });
          },
          'expired-callback': () => {
            setCaptchaToken('');
          },
          'error-callback': () => {
            setCaptchaToken('');
          },
        });

        captchaWidgetIdRef.current = widgetId;
      });
    }

    init();

    return () => {
      cancelled = true;
      setCaptchaToken('');

      // Очистка при закрытии
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
        } catch (e) {
          // Игнорируем ошибки при очистке
        }
      }
      captchaWidgetIdRef.current = null;
    };
  }, [isOpen, recaptchaSiteKey]);

  const handleClose = useCallback(() => {
    setEmail('');
    setIsLoading(false);
    setIsSuccess(false);
    setError('');
    setCaptchaToken('');
    // Сбрасываем CAPTCHA
    if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(captchaWidgetIdRef.current);
    }
    captchaWidgetIdRef.current = null;
    onClose();
  }, [onClose]);

  // Esc закрывает модалку, как и остальные диалоги приложения
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="ds-modal-overlay auth-modal"
      role="presentation"
      onMouseDown={(e) => {
        overlayPressRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const onOverlay = e.target === e.currentTarget;
        if (onOverlay && overlayPressRef.current && !isLoading) handleClose();
        overlayPressRef.current = false;
      }}
    >
      <div
        className="ds-modal auth-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        aria-busy={isLoading || undefined}
      >
        <div className="ds-modal__head auth-modal__head">
          <img src="/images/infiano.png" alt="" aria-hidden="true" className="auth-modal__logo" />
          <div className="auth-modal__titles">
            <span className="auth-modal__eyebrow">Welcome back</span>
            <h2 id="auth-modal-title">Sign In</h2>
          </div>
          <button type="button" className="close-modal" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="ds-modal__body">
          {!isSuccess ? (
            <>
              <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
                <div className="ds-form-group form-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    type="email"
                    id="email"
                    pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                    title="Enter a valid email with domain, e.g., user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email address"
                    disabled={isLoading}
                    autoFocus
                  />
                  <div className="ds-form-hint form-explanation">
                    We'll email you a secure, one-time link. No password needed.
                  </div>
                </div>
                {recaptchaSiteKey && (
                  <div id="recaptcha-container" style={{ display: 'flex', justifyContent: 'center' }} />
                )}
                {error && (
                  <div className="ds-alert ds-alert--error error-message" role="alert">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  className="ds-btn ds-btn--primary submit-button magic-link-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="ds-spinner" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    'Sign in via email'
                  )}
                </button>
              </form>

              <div className="auth-divider divider">
                <span className="divider-text">or</span>
              </div>

              <button
                type="button"
                className="ds-btn ds-btn--secondary google-auth-button"
                onClick={handleGoogleAuth}
                disabled={isLoading}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            </>
          ) : (
            <div className="success-message auth-success">
              <div className="auth-success__icon" aria-hidden="true">
                <i className="fas fa-check" />
              </div>
              <h3>Magic Link Sent!</h3>
              <p>Check your email and follow the link to sign{'\u00A0'}in.</p>
              <p className="ds-form-hint form-explanation">Didn't get it? Check spam or Promotions folder.</p>
              <button type="button" className="ds-btn ds-btn--secondary submit-button" onClick={handleClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
